import { useEffect, useRef } from "preact/hooks";
import { Fragment, type ComponentChildren } from "preact";

/**
 * Native <dialog> wrapper: opens modally when `open`, calls onClose on Escape / backdrop.
 * The body scrolls; an `.actions` row placed last stays pinned to the bottom (CSS sticky).
 */
export function Modal(props: { open: boolean; onClose: () => void; children: ComponentChildren }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (props.open && !d.open) { d.showModal(); d.querySelector(".dialog-body")?.scrollTo(0, 0); }
    if (!props.open && d.open) d.close();
  }, [props.open]);
  return (
    <dialog
      ref={ref}
      onClose={props.onClose}
      onClick={(e) => { if (e.target === ref.current) props.onClose(); }}
    >
      <div class="dialog-body" onClick={(e) => e.stopPropagation()}>{props.children}</div>
    </dialog>
  );
}

export interface ConfirmState {
  title: string;
  message: string;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => void;
}

export function ConfirmDialog(props: { state: ConfirmState | null; onClose: () => void }) {
  const s = props.state;
  return (
    <Modal open={s !== null} onClose={props.onClose}>
      {s && (
        <>
          <h2>{s.title}</h2>
          <div style={{ fontSize: 14, color: "var(--ink-2)" }}>{s.message}</div>
          <div class="actions">
            <button class="secondary" onClick={props.onClose}>Cancel</button>
            <button class={s.danger ? "danger" : ""} onClick={() => { s.onConfirm(); props.onClose(); }}>{s.confirmLabel}</button>
          </div>
        </>
      )}
    </Modal>
  );
}

export interface SheetAction {
  label: string;
  danger?: boolean;
  /** Draw a divider above this action (groups bean actions apart from app-wide ones). */
  dividerBefore?: boolean;
  onSelect: () => void;
}

/** A short list of actions for one item (what the ⋯ buttons open). */
export function ActionSheet(props: { title: string | null; actions: SheetAction[]; onClose: () => void }) {
  return (
    <Modal open={props.title !== null} onClose={props.onClose}>
      {props.title !== null && (
        <>
          <h2 style={{ fontSize: 16 }}>{props.title}</h2>
          <div class="sheet">
            {props.actions.map((a) => (
              <Fragment key={a.label}>
                {a.dividerBefore && <hr class="sheet-divider" />}
                <button type="button" class={a.danger ? "danger block" : "secondary block"}
                        onClick={() => { props.onClose(); a.onSelect(); }}>{a.label}</button>
              </Fragment>
            ))}
          </div>
          <div class="actions"><button class="ghost" type="button" onClick={props.onClose}>Cancel</button></div>
        </>
      )}
    </Modal>
  );
}
